import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FaRegPaperPlane } from 'react-icons/fa';
import ModalLayout from '../layouts/ModalLayout';
import { RootState } from '../../../redux/reducers';
import { Connection, ConnectionType } from '../../../redux/slices/connectionsSlice';
import localContactService from '../../../services/localContactService';
import useBusinessTranslation from '../../../hooks/useBusinessTranslation';
import IconBadge from '../icons/IconBadge';
import CancelSaveButton from '../misc/CancelSaveButton';
import ConnectionsList from '../misc/ConnectionsList';
import AddEmailsBeforeInviteModal from './AddEmailsBeforeInviteModal';

interface LocalContactInviteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export default function LocalContactInviteModal({
  open,
  setOpen,
}: LocalContactInviteModalProps): JSX.Element {
  const { t } = useBusinessTranslation();
  const connections = useSelector((state: RootState) => state.connections).filter(
    (c: Connection) => c.type === ConnectionType.LOCAL_CONTACT && !c.inviteSent,
  );
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [addEmailsModalOpen, setAddEmailsModalOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
  }, [open]);

  const handleSubmit = async (): Promise<void> => {
    const selectedConnections = connections.filter((c) => selected.includes(c.id));
    const contactsWithEmails = selectedConnections.filter((c) => c.fields.EMAIL);
    const contactsWithoutEmails = selectedConnections.filter((c) => !c.fields.EMAIL);

    setLoading(true);
    try {
      if (contactsWithEmails.length > 0) {
        const contactsWithEmailsIds = contactsWithEmails.map((c) => c.id);
        await localContactService.inviteContacts(contactsWithEmailsIds);
        
        if (contactsWithoutEmails.length === 0) {
          toast.success(t('toast.success.inviteSent', { count: contactsWithEmails.length }));
          setOpen(false);
          return;
        }
        toast.success(t('toast.success.inviteSent', { count: contactsWithEmails.length }));
      }

      if (contactsWithoutEmails.length > 0) {
        setAddEmailsModalOpen(true);
      } else {
        setOpen(false);
      }
    } catch (error) {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailsAdded = (): void => {
    setAddEmailsModalOpen(false);
    setOpen(false);
  };

  const selectedConnectionsWithoutEmails = connections.filter(
    (c) => selected.includes(c.id) && !c.fields.EMAIL
  );

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      className="h-a z-50 mx-6 flex w-fit min-w-[450px] max-w-[700px] flex-col rounded-[20px] bg-secondary-50 px-10 py-6 pb-4 pt-5">
      <div className="flex w-full">
        <IconBadge icon={FaRegPaperPlane} />

        <div className="mb-6 ml-4 flex w-full flex-col">
          <h1 className="text-2xl">{t('component.modal.localContactInvite.title')}</h1>
          <p>{t('component.modal.localContactInvite.subtitle')}</p>
        </div>
      </div>

      <ConnectionsList
        connections={connections.map(({ id, fields: { FIRST_NAME, LAST_NAME } }) => ({
          id,
          alias: `${FIRST_NAME}+${LAST_NAME}`,
        }))}
        selected={selected}
        setSelected={setSelected}
      />

      <div className="mt-6 flex w-full justify-end">
        <CancelSaveButton
          disabled={!selected.length || loading}
          onCancel={() => setOpen(false)}
          onSave={handleSubmit}
          buttonText={
            loading ? t('component.modal.addAdmin.loading') : t('component.modal.addAdmin.invite')
          }
        />
      </div>

      <AddEmailsBeforeInviteModal
        open={addEmailsModalOpen}
        setOpen={setAddEmailsModalOpen}
        contactsWithoutEmails={selectedConnectionsWithoutEmails}
        onEmailsAdded={handleEmailsAdded}
      />
    </ModalLayout>
  );
}
